import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import { getLocalDateString, formatDateDDMMYYYY } from "../../util/formatDate";

const pages = [{ name: "Mis consultas", href: "#", current: true }];


const getPatientId = (c) =>
    typeof c.patient === "object" ? c.patient?.id : c.patient;

const getDoctorName = (c) => {
    if (typeof c.doctor === "object") {
        return `${c.doctor?.firstName || ""} ${c.doctor?.lastName || ""}`.trim();
    }
    const me = JSON.parse(localStorage.getItem("BDOuserData") || "null");
    return me ? `${me.firstName} ${me.lastName}` : "";
};

const getDiagnoses = (c) =>
    c.general?.diagnoses?.map((d) => d.name).join(", ") ||
    c.diagnoses ||
    "--";

export default function MyConsultationsPage() {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [date, setDate] = useState(getLocalDateString());
    const [consultations, setConsultations] = useState([]);
    const [patientNames, setPatientNames] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.myConsultations
            .getMulti(null, null, null, null, { date })
            .then(setConsultations)
            .catch(() => setConsultations([]))
            .finally(() => setLoading(false));
    }, [date]);

    useEffect(() => {
        const idsToFetch = [
            ...new Set(
                consultations
                    .filter((c) => typeof c.patient !== "object")
                    .map((c) => c.patient)
                    .filter((id) => id && !patientNames[id])
            ),
        ];
        if (idsToFetch.length === 0) return;
        Promise.all(
            idsToFetch.map((id) =>
                api.patients
                    .getOne(id)
                    .then((p) => [id, `${p.firstName} ${p.lastName}`])
                    .catch(() => [id, `Paciente #${id}`])
            )
        ).then((pairs) => {
            setPatientNames((prev) => ({
                ...prev,
                ...Object.fromEntries(pairs),
            }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consultations]);

    const getPatientName = (c) =>
        typeof c.patient === "object"
            ? `${c.patient?.firstName || ""} ${c.patient?.lastName || ""}`.trim()
            : patientNames[c.patient] || "Cargando...";

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={() =>
                        setSidebarOpenDesktop((v) => !v) ||
                        setSidebarOpen((v) => !v)
                    }
                    mobileBackTo="/patients"
                    mobileBackLabel="Pacientes"
                    mobileTitle="Mis consultas"
                />
            }
        >
            <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-4 lg:px-6 lg:py-6">
                <section className="flex flex-col gap-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                            Mis consultas
                        </p>
                        <h1 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white sm:text-2xl">
                            Consultas guardadas
                        </h1>
                    </div>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-3 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                </section>

                {loading ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Cargando...
                    </p>
                ) : consultations.length === 0 ? (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            No hay consultas guardadas para{" "}
                            {formatDateDDMMYYYY(date)}.
                        </p>
                    </section>
                ) : (
                    <>
                        <div className="space-y-3 lg:hidden">
                            {consultations.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/patients/${getPatientId(c)}/consultations/${c.id}`}
                                    className="block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-primary/30 hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="text-sm font-semibold text-primary">
                                            {getPatientName(c)}
                                        </p>
                                        {getDoctorName(c) && (
                                            <p className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                                                {getDoctorName(c)}
                                            </p>
                                        )}
                                    </div>
                                    <p className="mt-1 truncate text-sm text-slate-700 dark:text-slate-200">
                                        {getDiagnoses(c)}
                                    </p>
                                </Link>
                            ))}
                        </div>

                        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 shadow-sm dark:border-slate-800 lg:block">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-900">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            Paciente
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            Doctor
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            Diagnostico
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-950">
                                    {consultations.map((c) => (
                                        <tr
                                            key={c.id}
                                            onClick={() =>
                                                navigate(
                                                    `/patients/${getPatientId(c)}/consultations/${c.id}`
                                                )
                                            }
                                            className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900"
                                        >
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-primary">
                                                {getPatientName(c)}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                                                {getDoctorName(c) || "--"}
                                            </td>
                                            <td className="max-w-xs truncate px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                                                {getDiagnoses(c)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </PatientModuleLayout>
    );
}
